import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { formatAvailability, isAvailableNow } from '@goaltree/backend/convex/lib/coachAvailability'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@goaltree/ui/components/dropdown-menu'
import { Sheet, SheetContent, SheetTitle } from '@goaltree/ui/components/sheet'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import {
  ChevronDown,
  ChevronRightIcon,
  MoreHorizontal,
  Pin,
  SettingsIcon,
  Trash2,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { create } from 'zustand'

import { CoachSheetHeader, SheetCloseButton, UserBubble } from '@/components/coach-sheet-parts'
import { NoteComposer } from '@/components/coaching/coach-notes'
import { firstName } from '@/components/coaching/coach-utils'
import { timeAgo } from '@/components/groves/grove-utils'
import { UserAvatar } from '@/components/user-avatar'
import { goalUrl } from '@/lib/utils'

export const useCoachNotesOpen = create<{ open: boolean }>(() => ({ open: false }))
const setOpen = (open: boolean) => useCoachNotesOpen.setState({ open })
export const openCoachNotes = () => setOpen(true)

export function useCoachInbox(enabled = true) {
  return useQuery(api.coaching.coachInbox, enabled ? {} : 'skip')
}

export function PresenceDot({ on }: { on: boolean }) {
  return (
    <span
      className={cn(
        'absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full ring-2 ring-background',
        on ? 'bg-green-500' : 'bg-muted-foreground/50',
      )}
    />
  )
}

// The human coach's face with the green coach ring; `dot` marks an unread note.
export function HumanCoachAvatar({
  image,
  name,
  size,
  dot,
  online,
  ring = 'ring-2 ring-coach',
  className,
}: {
  image: string | null
  name: string
  size: number
  dot?: boolean
  online?: boolean
  ring?: string
  className?: string
}) {
  return (
    <span
      className={cn('relative inline-flex shrink-0', className)}
      style={{ width: size, height: size }}
    >
      <UserAvatar
        image={image}
        name={name}
        size={size}
        className={cn('size-full rounded-full object-cover', ring)}
      />
      {online !== undefined && <PresenceDot on={online} />}
      {dot && (
        <span className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-coach ring-2 ring-background" />
      )}
    </span>
  )
}

function CoachThread({
  linkId,
  pinnedId,
  coachName,
  coachImage,
}: {
  linkId: Id<'coachLinks'>
  pinnedId?: Id<'coachNotes'>
  coachName: string
  coachImage: string | null
}) {
  const { t, i18n } = useTranslation()
  const notes = useQuery(api.coaching.notes, { linkId, goalId: null })
  const remove = useMutation(api.coaching.deleteNote).withOptimisticUpdate((store, { noteId }) => {
    const args = { linkId, goalId: null }
    const value = store.getQuery(api.coaching.notes, args)
    if (value)
      store.setQuery(
        api.coaching.notes,
        args,
        value.filter((n) => n._id !== noteId),
      )
  })
  const end = useRef<HTMLDivElement>(null)
  const count = notes?.length
  // Block on purpose. scrollIntoView() returns a Promise, and React calls an
  // effect's return value when this thread unmounts (hiding the sheet).
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' })
  }, [count])
  const shown = notes?.filter((n) => n._id !== pinnedId)
  if (shown === undefined) return <div className="h-16 animate-pulse rounded-xl bg-muted/40" />
  return (
    <div className="flex flex-col gap-4">
      {shown.length === 0 && (
        <p className="py-2 text-center text-xs text-muted-foreground/60">
          {t('coaching.notes.empty')}
        </p>
      )}
      {shown.map((n) => {
        const time = timeAgo(n.createdAt, i18n.language)
        const canDelete = n.mine && !n._id.startsWith('optimistic-')
        if (n.fromCoach)
          return (
            <div key={n._id} className="flex items-start gap-2">
              <HumanCoachAvatar image={coachImage} name={coachName} size={22} className="mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words text-foreground">
                  {n.body}
                </p>
                <p className="mt-0.5 text-[10.5px] text-muted-foreground/70">{time}</p>
              </div>
            </div>
          )
        return (
          <div key={n._id} className="group flex flex-col items-end gap-0.5 self-end">
            <UserBubble>{n.body}</UserBubble>
            <p className="flex items-center gap-1.5 text-[10.5px] text-muted-foreground/70">
              {canDelete && (
                <button
                  type="button"
                  onClick={() => void remove({ noteId: n._id }).catch(() => {})}
                  aria-label={t('common.delete')}
                  className="cursor-pointer opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground focus-visible:opacity-100 [@media(pointer:coarse)]:opacity-100"
                >
                  <Trash2 className="size-3" />
                </button>
              )}
              {time}
            </p>
          </div>
        )
      })}
      <div ref={end} />
    </div>
  )
}

// App-wide human-coach conversation sheet for clients, opened via openCoachNotes().
export function MyCoachNotesSheet() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const open = useCoachNotesOpen((s) => s.open)
  const [goalsOpen, setGoalsOpen] = useState(false)
  const inbox = useCoachInbox()
  const markSeen = useMutation(api.coaching.markNotesSeen).withOptimisticUpdate((store) => {
    const value = store.getQuery(api.coaching.coachInbox, {})
    if (value) store.setQuery(api.coaching.coachInbox, {}, { ...value, unread: false })
  })
  const unread = !!inbox?.unread
  useEffect(() => {
    if (open && unread) void markSeen().catch(() => {})
  }, [open, unread, markSeen])
  if (!inbox) return null
  const { coach, availability } = inbox
  const name = firstName(coach.name)
  const close = () => setOpen(false)
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-md" showCloseButton={false}>
        <SheetTitle className="sr-only">{t('coaching.goal.notesWith', { name })}</SheetTitle>
        <CoachSheetHeader
          avatar={
            <HumanCoachAvatar
              image={coach.image}
              name={coach.name}
              size={48}
              online={availability ? isAvailableNow(availability) : undefined}
            />
          }
          name={name}
          subtitle={
            availability
              ? `${t('coaching.coaches.humanDesc')} · ${formatAvailability(availability, i18n.language)}`
              : t('coaching.coaches.humanDesc')
          }
        >
          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label={t('coach.menu')}
              className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground data-popup-open:bg-muted data-popup-open:text-foreground"
            >
              <MoreHorizontal className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-xl p-1 shadow-2xl">
              <DropdownMenuItem
                onClick={() => {
                  close()
                  void navigate({ to: '/settings', hash: 'mycoach' })
                }}
                className="cursor-pointer gap-2 rounded-lg px-2.5 py-2"
              >
                <SettingsIcon className="size-3.5" />
                {t('settings.sec.mycoach')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <SheetCloseButton onClose={close} />
        </CoachSheetHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
          {inbox.pinned && (
            <div className="flex items-start gap-2 rounded-xl border border-coach/30 bg-coach/5 px-3 py-2">
              <Pin className="mt-0.5 size-3.5 shrink-0 text-coach-text" />
              <div className="min-w-0">
                <p className="text-[10px] font-bold tracking-wide text-coach-text uppercase">
                  {t('coaching.notes.focus')}
                </p>
                <p className="text-[13px] leading-snug whitespace-pre-wrap break-words text-foreground">
                  {inbox.pinned.body}
                </p>
              </div>
            </div>
          )}
          {inbox.goals.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
              <button
                type="button"
                onClick={() => setGoalsOpen((v) => !v)}
                aria-expanded={goalsOpen}
                className="flex w-full cursor-pointer items-center gap-2 px-3 py-2.5 text-left"
              >
                <span className="flex-1 text-[13px] font-semibold text-foreground">
                  {t('coaching.coaches.goalThreads')}
                </span>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {inbox.goals.length}
                </span>
                <ChevronDown
                  className={cn(
                    'size-4 text-muted-foreground transition-transform',
                    goalsOpen && 'rotate-180',
                  )}
                />
              </button>
              {goalsOpen && (
                <ul className="divide-y divide-border/60 border-t border-border/60">
                  {inbox.goals.map((g) => (
                    <li key={g.goalId}>
                      <button
                        type="button"
                        onClick={() => {
                          // Close first, then move. A Link flushes its click state
                          // after onClick and unmounts this sheet mid-navigation.
                          close()
                          void navigate({
                            to: '/goals/$goalId',
                            params: {
                              goalId: goalUrl(g.title, g.goalId).replace('/goals/', ''),
                            },
                          })
                        }}
                        className="flex w-full cursor-pointer items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{g.title}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            <span className={cn(g.last.fromCoach && 'text-coach-text')}>
                              {g.last.mine ? t('coaching.notes.you') : name}
                            </span>
                            {` · ${timeAgo(g.last.createdAt, i18n.language)} · ${g.last.body}`}
                          </p>
                        </div>
                        <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <CoachThread
            linkId={inbox.linkId}
            pinnedId={inbox.pinned?.goalId === null ? inbox.pinned._id : undefined}
            coachName={coach.name}
            coachImage={coach.image}
          />
        </div>
        <div className="shrink-0 border-t border-border/40 px-2 py-3">
          <NoteComposer
            boxed
            linkId={inbox.linkId}
            goalId={null}
            fromCoach={false}
            placeholder={t('coaching.notes.messageName', { name })}
          />
        </div>
      </SheetContent>
    </Sheet>
  )
}
